import * as React from 'react';
import { Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { cssClass } from '../../../style';
import { DataRoleSingleValue } from '../../../ts/ServerApi/types/IDataRole';
import { SynonymsChipPicker } from '../../utils/chip-pickers/SynonymChipPicker';
import { LazyListWidget } from '../../utils/lazy-list/LazyList';
import { formatValue, highlightSearch, matchesSearch } from '../../utils/searchUtils';
import { useIndexedState } from '../../utils/useIndexedState';
import { ValuesAndSynonymsHeader } from './ValuesAndSynonymsHeader';

const rowHeight = '39px';
const cssPrefix = 'DataroleValuesAndSynonymsList';
const rowStyle =  cssClass(cssPrefix, 'singleRow', {
  borderBottom: '1px solid rgb(235, 235, 235)',
  minHeight: rowHeight,
});

const valueStyle =  cssClass(cssPrefix, 'rowValue', {
  paddingLeft: CommonStyles.Sizes.DefaultSpace,
  display: 'inline-block',
  width: '33%',
  boxSizing: 'border-box',
  lineHeight: rowHeight,
});

const synonymsStyle =  cssClass(cssPrefix, 'rowSynonyms', {
  paddingLeft: CommonStyles.Sizes.DefaultSpace,
  display: 'inline-block',
  width: '67%',
  boxSizing: 'border-box',
  lineHeight: rowHeight,
  verticalAlign: 'bottom',
});

export interface DataRoleValuesAndSynonymsListProps {
  readonly valuesAndSynonyms: ReadonlyArray<DataRoleSingleValue>;
  readonly isEditing: boolean;
  readonly filterText: string;
}

export const DataRoleValuesAndSynonymsList: React.FC<DataRoleValuesAndSynonymsListProps> = (props: DataRoleValuesAndSynonymsListProps) => {
  const orderedValuesAndSynonyms = props.valuesAndSynonyms;
  const [synonyms, setSynonym] = useIndexedState(orderedValuesAndSynonyms.map(v => v.valueSynonyms));
  const displayedValuesAndSynonyms = orderedValuesAndSynonyms
    .map((v, i) => ({...v, valueSynonyms: synonyms[i]}))
    .filter(v => matchesSearch(v, props.filterText));
  return <>
    <ValuesAndSynonymsHeader/>
    <LazyListWidget
    rows={displayedValuesAndSynonyms}
    rowHeight={39}
    rowToComponent={props.isEditing? renderRoleValueWithEditableSynonyms(setSynonym, props.filterText) : renderRoleValueWithConstSynonyms(props.filterText)}/></>;
};


const renderRoleValueWithEditableSynonyms = (setSynonym: (row: number, values: string[]) => void, searchText: string) => (valueWithSynonyms: DataRoleSingleValue, row: number): JSX.Element => (
  <div className={rowStyle}>
    <div className={valueStyle}>{highlightSearch(formatValue(valueWithSynonyms), searchText)}</div>
    <div className={synonymsStyle} style={{padding: 0}}>
      <SynonymsChipPicker
        placeholder={Messages.dataRoleDefinitionSubspace_Values_SynonymEdit_Placeholder({VALUE: formatValue(valueWithSynonyms)})}
        label={Messages.dataRoleDefinitionSubspace_Values_SynonymEdit_Label({VALUE: formatValue(valueWithSynonyms)})}
        testId={`synonyms-for-${JSON.stringify(valueWithSynonyms)}`}
        values={valueWithSynonyms.valueSynonyms || []}
        onChange={(values: string[]) => setSynonym(row, values)}
        style={{border: 'none'}}
        chipContentFormatter={(value: string) => highlightSearch(value, searchText)}
        getErrorMessage={(e: string) => ''}/>
    </div>
  </div>
);

const renderRoleValueWithConstSynonyms = (searchText: string) => (valueWithSynonyms: DataRoleSingleValue): JSX.Element => (
  <div className={rowStyle}>
    <div className={valueStyle}>{highlightSearch(formatValue(valueWithSynonyms), searchText)}</div>
    <div className={synonymsStyle}>
      {highlightSearch((valueWithSynonyms.valueSynonyms || []).join(', '), searchText)}
      </div>
  </div>
);

